import React from 'react';
import { Document, Font, Image, Page, StyleSheet, Text, View } from '@react-pdf/renderer';

import logo from '../../../../app/coop-logo.png';
import merriweatherRegular from './Merriweather-Regular.ttf';
import merriweatherBold from './Merriweather-Bold.ttf';
import { formatCurrency } from '../../../billing/timesheets/BillingTools';
import { all } from '../../../../app/data/validate';

Font.register({
    family: 'Merriweather',
    fonts: [
        { src: merriweatherRegular },
        { src: merriweatherBold },
    ],
})

const css = StyleSheet.create({
    page: {
        color: '#212529',
        padding: '16pt',
        fontFamily: 'Merriweather',
    },
    logo: {
        fontSize: '16pt',
        fontWeight: 'bold',
        color: '#339989',
        marginBottom: '8px',
    },
    header: {
        fontSize: '10pt'
    },
    category: {
        fontWeight: 'bold',
        marginBottom: '4px',
        marginTop: '8px',
        fontSize: '11pt',
    },
    description: {
        display: 'flex',
        justifyContent: 'flex-start',
    },
    row: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        fontSize: '10pt'
    },
    cell: {
        flex: 1,
        minWidth: '0px',
    },
    s1: {
        flex: 1
    },
    s2: {
        flex: 2
    },
    s3: {
        flex: 3
    },
    s4: {
        flex: 4
    },
    s5: {
        flex: 5
    },
    s6: {
        flex: 6
    },
    s7: {
        flex: 7
    },
    s8: {
        flex: 8
    },
    right: {
        display: 'flex',
        justifyContent: 'flex-end',
        textAlign: 'right',
    },
    left: {
        display: 'flex',
        justifyContent: 'flex-start'
    }
})

const getItemDescription = item => {
    if (item.section === 'accommodation') return `Accommodation ${item.description}`;
    return item.description;
}

const getItemMeasure = item => {
    if (item.measure === 'total') return `cost`;
    return item.measure + 's';
}

const getItemQuantity = item => {
    if (item.measure === 'total') return `-`;
    return item.quantity;
}

const CategoryHeader = ({ title, css, numberStyleSmall, numberStyleLarge }) => {
    return (
        <React.Fragment>
            <Text style={css.category}>{title}</Text>
            <View style={{ ...css.row, backgroundColor: '#adb5bd', fontWeight: 'semibold' }}>
                <Text style={{ ...css.cell, ...css.s6 }}>Description</Text>
                <Text style={numberStyleSmall}>Quantity</Text>
                <Text style={numberStyleSmall}>Unit</Text>
                <Text style={numberStyleLarge}>Rate</Text>
                <Text style={numberStyleLarge}>Net Amount</Text>
            </View>
        </React.Fragment>
    );
}

const QuotePDF = ({ digest }) => {
    const { intermediary, referral, request, courtCase, quote, notes } = digest;

    if (!all(intermediary, referral, request, courtCase, quote)) return <Document></Document>

    const numberStyleLarge = { ...css.cell, ...css.s3, ...css.right };
    const numberStyleSmall = { ...css.cell, ...css.s3, ...css.right };
    const netTotal = quote.netTotal();
    const grandTotal = quote.grandTotal();
    const vat = grandTotal - netTotal;

    const needs = referral.vulnerabilities?.length > 0 ?
        referral.vulnerabilities.map(v => v.description)
        : [notes?.clientNeeds?.text || 'Unspecified'];

    const { postcode = '' } = intermediary.preferences?.location?.city || {};

    return <Document>
        <Page size="A4" style={css.page}>
            <View style={css.row}>
                <View style={{ flex: 6 }}>
                    <Text style={{ ...css.logo }}>The Intermediary Cooperative</Text>
                    <Text>Quotation requested by {referral.referrer?.name || '?'} at {request.organization}</Text>
                    <Text>Reference {referral.bookingReference} / Court Case Number {courtCase.courtCaseNumber}</Text>
                </View>
                <Image src={logo} style={{ flex: 6, justifyContent: 'flex-end', maxWidth: '100px', height: 'auto' }} />
            </View>
            <View style={{ ...css.header, marginBottom: '4px' }}>
                <Text style={{ fontWeight: 'bold' }}>Your Intermediary</Text>
                <Text>{intermediary.profile?.title}</Text>
                <Text>{intermediary.profile?.email}</Text>
                <Text>{intermediary.profile?.phoneNumber}</Text>
                <Text>{postcode ? `Located in ${postcode.split(' ')[0]}` : ''}</Text>

                <View style={{ ...css.row, marginTop: '8px' }}>
                    <View style={{ flex: 6 }}>
                        <Text style={{ marginTop: '4px', fontWeight: 'bold' }}>Client</Text>
                        <Text>{courtCase.serviceUserName || 'Unspecified'}, Age {referral.client?.age || 'Unspecified'}</Text>
                        <Text style={{ marginTop: '4px', fontWeight: 'bold' }}>Vulnerabilities / Communication Needs</Text>
                        {
                            needs.map((description, index) => {
                                return <Text key={`need-${index}`}>- {description}</Text>
                            })
                        }
                    </View>
                    <View style={{ flex: 6 }}>
                        <Text style={{ marginTop: '4px', fontWeight: 'bold' }}>Service Location</Text>
                        <Text>{referral.location?.name}</Text>
                        <Text style={{ marginTop: '4px', fontWeight: 'bold' }}>Timescales</Text>
                        <Text>{notes?.dates?.text || request.dates || 'To be determined'}</Text>
                    </View>
                </View>
            </View>
            <CategoryHeader title="Intermediary Services" css={css} numberStyleLarge={numberStyleLarge} numberStyleSmall={numberStyleSmall} />
            {
                quote.items().filter(item => item.quantity > 0 && item.category === 'services').map(item => {
                    return <View style={css.row} key={item.key}>
                        <Text style={{ ...css.cell, ...css.s6 }}>{item.description}</Text>
                        <Text style={numberStyleSmall}>{item.quantity}</Text>
                        <Text style={numberStyleSmall}>{item.measure}s</Text>
                        <Text style={numberStyleSmall}>{formatCurrency(item.rate)}</Text>
                        <Text style={numberStyleLarge}>{formatCurrency(item.amount)}</Text>
                    </View>;
                })
            }
            <View style={{ ...css.row, fontWeight: 'bold' }}>
                <Text style={{ ...css.cell, ...css.s8, marginTop: '6px' }}>Total Services</Text>
                <Text style={{ ...numberStyleLarge, marginTop: '6px' }}>{formatCurrency(quote.netCategoryTotal('services'))}</Text>
            </View>
            <CategoryHeader title="Intermediary Expenses" css={css} numberStyleLarge={numberStyleLarge} numberStyleSmall={numberStyleSmall} />
            {
                quote.items().filter(item => item.quantity > 0 && item.category === 'expenses').map(item => {
                    return <View style={css.row} key={item.key}>
                        <Text style={{ ...css.cell, ...css.s6 }}>{getItemDescription(item)}</Text>
                        <Text style={numberStyleSmall}>{getItemQuantity(item)}</Text>
                        <Text style={numberStyleSmall}>{getItemMeasure(item)}</Text>
                        <Text style={numberStyleSmall}>{formatCurrency(item.rate)}</Text>
                        <Text style={numberStyleLarge}>{formatCurrency(item.amount)}</Text>
                    </View>;
                })
            }
            <View style={{ ...css.row, fontWeight: 'bold' }}>
                <Text style={{ ...css.cell, ...css.s8, marginTop: '6px' }}>Total Expenses</Text>
                <Text style={{ ...numberStyleLarge, marginTop: '6px' }}>{formatCurrency(quote.netCategoryTotal('expenses'))}</Text>
            </View>
            <View style={{ ...css.row, fontWeight: 'bold' }}>
                <Text style={{ ...css.cell, ...css.s8, marginTop: '10px' }}>Net Quotation Total</Text>
                <Text style={{ ...numberStyleLarge, marginTop: '10px' }}>{formatCurrency(netTotal)}</Text>
            </View>
            <View style={{ ...css.row, fontWeight: 'bold' }}>
                <Text style={{ ...css.cell, ...css.s8, marginTop: '2px' }}>VAT @ 20%</Text>
                <Text style={{ ...numberStyleLarge, marginTop: '2px' }}>{formatCurrency(vat)}</Text>
            </View>
            <View style={{ ...css.row, fontWeight: 'ultrabold' }}>
                <Text style={{ ...css.cell, ...css.s8, marginTop: '2px' }}>Gross Quotation Total</Text>
                <Text style={{ ...numberStyleLarge, marginTop: '2px' }}>{formatCurrency(grandTotal)}</Text>
            </View>
            <View style={{ marginTop: '16px' }}>
                <Text style={{ fontWeight: 'bold', fontSize: '11pt' }}>Notes</Text>
                {
                    quote.items().filter(item => item.note).map((item, index) => {
                        return <View style={{ ...css.row }} key={`note-${index}`}>
                            <Text style={{ ...css.cell, ...css.s4, marginTop: '2px' }}>{item.description}</Text>
                            <Text style={{ ...css.cell, ...css.s8, marginTop: '2px' }}>"{item.note}"</Text>
                        </View>
                    })
                }
            </View>
            <View style={{ marginTop: '16px', fontSize: '11pt' }}>
                <Text style={{ fontWeight: 'bold' }}>Supplier Details</Text>
                <Text>Name: The Intermediary Group Ltd. T/A The Intermediary Cooperative</Text>
                <Text>Supplier Number: 5149847</Text>
            </View>

        </Page>
    </Document>
};

export default QuotePDF;